import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IonContent, ModalController } from '@ionic/angular';
import { UploadDocumentRequest } from '../models/upload-document-reqest';
import { UploadItem } from '../models/upload-item';
import { ModalRole } from '../models/modal-roles';
import { concatMap, from, tap, map, catchError, EMPTY, finalize } from 'rxjs';
import { NodeItem } from '../../api/models';
import { StateService } from '../../services/state.service';
import { CameraService } from '../../services/camera.service';
import { ToolService } from '../../services/tool.service';

@Component({
  selector: 'lib-upload-modal',
  templateUrl: './upload-modal.component.html',
  styleUrls: ['./upload-modal.component.scss'],
  standalone: false,
})
export class UploadModalComponent implements OnInit {
  private translate = inject(TranslateService);

  files: UploadItem[] = [];
  invalidFileInfos: string[] = [];
  uploadIsRunning: boolean = false;
  uploadCounter: number = 0;
  currentFolder: NodeItem;
  fileList: FileList;
  failedUploads: UploadItem[] = [];
  @ViewChild('modalContent', { static: false })
  modalContent!: IonContent;

  constructor(
    public state: StateService,
    public cameraService: CameraService,
    public tools: ToolService,
    public modalController: ModalController
  ) {}

  ngOnInit() {
    if (this.fileList) {
      this.files = [];
      Array.from(this.fileList).forEach((file) => {
        const splittedName = file.name.split('.');
        const fileType =
          '.' + splittedName[splittedName.length - 1].toLowerCase();

        const item: UploadItem = {
          file,
          blob: undefined,
          displayName: file.name.substring(0, file.name.lastIndexOf('.')),
          fileType,
        };

        if (!this.state.supportedFileTypes.includes(fileType)) {
          this.invalidFileInfos.push(
            this.translate.instant('upload.typeNotSupported', {
              type: fileType,
              name: item.file.name,
              supported: this.state.supportedFileTypes.join(', '),
            })
          );
        } else if (file.size / 1000 / 1000 < 100) {
          this.files.push(item);
        } else {
          this.invalidFileInfos.push(
            this.translate.instant('upload.sizeExceeded', {
              name: item.file.name,
              size: Math.floor(file.size / 1000 / 1000),
            })
          );
        }
      });
    }
  }

  public removeFile(index: number) {
    this.files.splice(index, 1);
    if (this.files.length == 0) {
      this.invalidFileInfos = [];
      this.close();
    }
  }

  getFileSize(size: number): string {
    return (size / 1000).toFixed(0);
  }

  startUpload() {
    this.modalContent.scrollToTop(300);
    this.failedUploads = [];
    this.uploadIsRunning = true;
    const item = this.files[0];
    var folderId = this.currentFolder?.id ?? ' ';
    const isBlobUpload: boolean = item.file == null;
    from(this.files)
      .pipe(
        map((x) => {
          const req: UploadDocumentRequest = {
            file: isBlobUpload ? x.blob : x.file,
            fileName: isBlobUpload
              ? new Date().toISOString() + item.fileType
              : x.file.name,
            displayName: x.displayName,
            folderId,
            constructionSite: this.currentFolder?.constructionSiteId,
            sizeInBytes: isBlobUpload ? x.blob.size : x.file.size,
          };

          return req;
        }),
        concatMap((req) =>
          this.state.uploadFile(req).pipe(
            tap(() => {
              this.tools.showInfoToast(
                this.translate.instant('upload.fileSuccess', {
                  name: req.displayName,
                })
              );
            }),
            catchError((err) => {
              this.failedUploads.push(
                this.files.find((x) => x.file == req.file)
              );
              this.state.handleError(err);
              return EMPTY;
            }),
            finalize(() => {
              this.uploadDone();
            })
          )
        )
      )
      .subscribe({
        complete: () => {},
      });
  }

  uploadDone() {
    this.uploadCounter++;
    if (this.uploadCounter == this.files.length) {
      if (this.failedUploads.length > 0) {
        this.uploadIsRunning = false;
        this.uploadCounter = 0;
        this.invalidFileInfos = [];
        this.files = this.failedUploads;
      } else {
        this.tools.showInfoToast(
          this.translate.instant('upload.completeSuccess')
        );
        this.modalController.dismiss(null, ModalRole.Confirm);
      }
    }
  }

  close() {
    this.modalController.dismiss(null, ModalRole.Cancel);
  }
}
